export type WeatherMetric = 'temperature' | 'humidity' | 'windSpeed' | 'rain' | 'pressure' | 'cloudCover';

export interface CurrentWeather {
  temperature: number | null;
  condition: string;
  humidity: number | null;
  windSpeed: number | null;
  rain: number | null;
  precipitation: number | null;
  cloudCover: number | null;
  pressure: number | null;
}

export interface WeatherForecastDay {
  date: string;
  temperature: number | null;
  humidity: number | null;
  windSpeed: number | null;
  rain: number | null;
  pressure: number | null;
  cloudCover: number | null;
}

export interface WeatherData {
  current: CurrentWeather;
  weeklyForecast: WeatherForecastDay[];
}

const WEATHER_TIMEOUT_MS = 3500;

const fetchJsonWithTimeout = async (url: string) => {
  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => controller.abort(), WEATHER_TIMEOUT_MS);

  try {
    const response = await fetch(url, {
      headers: { Accept: 'application/json' },
      signal: controller.signal,
    });

    if (!response.ok) {
      throw new Error(`Weather API returned ${response.status}`);
    }

    return response.json();
  } finally {
    window.clearTimeout(timeoutId);
  }
};

const toNumber = (value: unknown): number | null => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const round = (value: number | null) => (typeof value === 'number' ? Number(value.toFixed(1)) : null);

const getWttrWeather = async (latitude: number, longitude: number): Promise<WeatherData> => {
  const payload = await fetchJsonWithTimeout(`https://wttr.in/${latitude},${longitude}?format=j1`);
  const current = payload?.current_condition?.[0] ?? {};
  const forecastItems = (payload?.weather ?? []).flatMap((day: any) =>
    (day.hourly ?? []).map((item: any) => ({
      date: `${day.date}T${String(item.time ?? '0').padStart(4, '0').replace(/(\d{2})$/, ':$1')}`,
      temperature: toNumber(item.tempC),
      humidity: toNumber(item.humidity),
      windSpeed: toNumber(item.windspeedKmph),
      rain: toNumber(item.precipMM),
      pressure: toNumber(item.pressure),
      cloudCover: toNumber(item.cloudcover),
    })),
  );

  return {
    current: {
      temperature: toNumber(current.temp_C),
      condition: current.weatherDesc?.[0]?.value ?? 'Current conditions',
      humidity: toNumber(current.humidity),
      windSpeed: toNumber(current.windspeedKmph),
      rain: toNumber(current.precipMM),
      precipitation: toNumber(current.precipMM),
      cloudCover: toNumber(current.cloudcover),
      pressure: toNumber(current.pressure),
    },
    weeklyForecast: forecastItems,
  };
};

const getMetNoWeather = async (latitude: number, longitude: number): Promise<WeatherData> => {
  const payload = await fetchJsonWithTimeout(`https://api.met.no/weatherapi/locationforecast/2.0/compact?lat=${latitude}&lon=${longitude}`);
  const timeseries = payload?.properties?.timeseries ?? [];
  const current = timeseries[0] ?? {};
  const currentDetails = current?.data?.instant?.details ?? {};

  return {
    current: {
      temperature: round(toNumber(currentDetails.air_temperature)),
      condition: current?.data?.next_1_hours?.summary?.symbol_code?.replace(/_/g, ' ') ?? 'Current conditions',
      humidity: round(toNumber(currentDetails.relative_humidity)),
      windSpeed: round(toNumber(currentDetails.wind_speed) == null ? null : Number(currentDetails.wind_speed) * 3.6),
      rain: round(toNumber(current?.data?.next_1_hours?.details?.precipitation_amount)),
      precipitation: round(toNumber(current?.data?.next_1_hours?.details?.precipitation_amount)),
      cloudCover: round(toNumber(currentDetails.cloud_area_fraction)),
      pressure: round(toNumber(currentDetails.air_pressure_at_sea_level)),
    },
    weeklyForecast: timeseries.map((item: any) => {
      const details = item?.data?.instant?.details ?? {};
      return {
        date: item.time,
        temperature: round(toNumber(details.air_temperature)),
        humidity: round(toNumber(details.relative_humidity)),
        windSpeed: round(toNumber(details.wind_speed) == null ? null : Number(details.wind_speed) * 3.6),
        rain: round(toNumber(item?.data?.next_1_hours?.details?.precipitation_amount)),
        pressure: round(toNumber(details.air_pressure_at_sea_level)),
        cloudCover: round(toNumber(details.cloud_area_fraction)),
      };
    }),
  };
};

export const getWeather = async (latitude: number, longitude: number): Promise<WeatherData> => {
  try {
    return await getMetNoWeather(latitude, longitude);
  } catch {
    return getWttrWeather(latitude, longitude);
  }
};
